import React, { useState, useMemo } from 'react';
import moment from 'moment';
import { RRule } from 'rrule';
import SVGColorBlock from './SVGColorBlock';
import { getScenePreviewColors, getSceneType } from './sceneUtils';
import {
	isAstroSchedule,
	hasAstroLocation,
	getAstroTimeForDay,
	getAstroTimesForDisplay,
	getPrimaryAstroBanner,
	getSystemTimezone,
} from './astroUtils';

// --- Helpers (refactored for perf: never expand full-month ranges for high-freq schedules) ---

function getBaseTime(schedule, system, dayMoment) {
  if (isAstroSchedule(schedule) && system && hasAstroLocation(system) && dayMoment) {
    const astroTime = getAstroTimeForDay(schedule, system, dayMoment);
    if (astroTime) return astroTime;
  }
  try {
    const bt = schedule.performAt && typeof schedule.performAt.toDate === 'function'
      ? schedule.performAt.toDate()
      : schedule.performAt;
    return bt ? new Date(bt) : null;
  } catch (e) {
    return null;
  }
}

function parseRuleDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string' && value.trim()) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  return null;
}

// Date-only "until" values from the editor should include the full calendar day.
function normalizeUntilDate(untilValue) {
  if (typeof untilValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(untilValue.trim())) {
    return moment(untilValue.substring(0, 10), 'YYYY-MM-DD').endOf('day').toDate();
  }
  const d = parseRuleDate(untilValue);
  if (!d) return null;
  // Firestore Timestamps from the date picker are UTC midnight on the chosen calendar date.
  const asUtc = moment.utc(d);
  if (
    asUtc.hour() === 0 &&
    asUtc.minute() === 0 &&
    asUtc.second() === 0 &&
    asUtc.millisecond() === 0
  ) {
    return moment(asUtc.format('YYYY-MM-DD'), 'YYYY-MM-DD').endOf('day').toDate();
  }
  const m = moment(d);
  return moment({ year: m.year(), month: m.month(), date: m.date() }).endOf('day').toDate();
}

function getRuleRaw(schedule) {
  const repeatRule = schedule.r_rule || schedule.rrule;
  if (!repeatRule) return null;
  return repeatRule.state || repeatRule;
}

// One-time when explicitly not repeating, or when there is no rule to expand.
// repeats === false wins over a stale r_rule so it cannot paint every day.
function isOneTimeSchedule(schedule) {
  if (schedule?.repeats === false) return true;
  return !(schedule?.r_rule || schedule?.rrule);
}

// Calendar day of the stored run instant. Missing performAt does not match any day.
function performAtMatchesDay(schedule, dayMoment) {
  const performAt = parseRuleDate(schedule?.performAt);
  if (!performAt || !dayMoment) return false;
  return moment(performAt).isSame(dayMoment, 'day');
}

function getNormalizedUntil(schedule) {
  const raw = getRuleRaw(schedule);
  if (!raw || raw.until == null || raw.until === '') return null;
  return normalizeUntilDate(raw.until);
}

function dayWithinScheduleBounds(schedule, dayMoment, baseTime) {
  if (baseTime && dayMoment.isBefore(moment(baseTime), 'day')) return false;
  const until = getNormalizedUntil(schedule);
  if (until && dayMoment.isAfter(moment(until), 'day')) return false;
  return true;
}

function prepareRRuleOpts(schedule, baseTime) {
  const repeatRule = schedule.r_rule || schedule.rrule;
  if (!repeatRule) return null;
  const rruleOpts = { ...(repeatRule.state || repeatRule) };
  delete rruleOpts.dtstart;
  delete rruleOpts.runmode;
  if (baseTime) rruleOpts.dtstart = baseTime;
  if (rruleOpts.freq !== undefined) {
    const f = Number(rruleOpts.freq);
    const freqMap = {
      0: RRule.YEARLY,
      1: RRule.MONTHLY,
      2: RRule.WEEKLY,
      3: RRule.DAILY,
      4: RRule.HOURLY,
      5: RRule.MINUTELY,
      6: RRule.SECONDLY,
    };
    rruleOpts.freq = freqMap[f] || f;
  }
  if (rruleOpts.interval !== undefined) {
    rruleOpts.interval = +rruleOpts.interval || 1;
  }
  if (rruleOpts.count !== undefined) {
    const count = Number(rruleOpts.count);
    if (!count || count <= 0) delete rruleOpts.count;
    else rruleOpts.count = count;
  }
  if (rruleOpts.until !== undefined) {
    const until = normalizeUntilDate(rruleOpts.until);
    if (until) rruleOpts.until = until;
    else delete rruleOpts.until;
  }
  if (Array.isArray(rruleOpts.byweekday) && rruleOpts.byweekday.length === 0) delete rruleOpts.byweekday;
  if (Array.isArray(rruleOpts.bymonth) && rruleOpts.bymonth.length === 0) delete rruleOpts.bymonth;
  return rruleOpts;
}

// Static check for dense high-freq daily schedules (e.g. every 5 min every day).
// No rrule execution, no occurrence generation. Just inspect options.
// Used to skip expensive per-day probes in the month grid.
function isHighFrequencyDaily(schedule) {
  const repeatRule = schedule.r_rule || schedule.rrule;
  if (!repeatRule) return false;
  const raw = repeatRule.state || repeatRule;
  const f = Number(raw.freq);
  const interval = Number(raw.interval) || 1;
  const byweekday = Array.isArray(raw.byweekday) ? raw.byweekday : [];
  const bymonth = Array.isArray(raw.bymonth) ? raw.bymonth : [];
  // If restricted to specific days/months, not "every day" dense.
  if (byweekday.length > 0 || bymonth.length > 0) return false;
  // Minutely or hourly with small interval => many runs per day, dense daily.
  if ((f === 5 /* MINUTELY */ || f === 4 /* HOURLY */) && interval <= 30) {
    return true;
  }
  return false;
}

function getOccurrencesInRange(schedule, rangeStart, rangeEnd, prebuiltRr = null, maxOcc = 500, system = null) {
  // Returns small list of {date, time: 'HH:mm'} only for the *given small range*.
  const results = [];
  const rangeStartM = moment(rangeStart);
  const rangeEndM = moment(rangeEnd);
  const dayMoment = rangeStartM.clone().startOf('day');

  if (isAstroSchedule(schedule) && system && hasAstroLocation(system)) {
    if (isOneTimeSchedule(schedule)) {
      if (!performAtMatchesDay(schedule, dayMoment)) return results;
      const astroTime = getAstroTimeForDay(schedule, system, dayMoment);
      if (astroTime) {
        const m = moment(astroTime);
        if (m.isBetween(rangeStartM, rangeEndM, 'minute', '[]')) {
          results.push({ date: m.toDate(), time: m.format('HH:mm') });
        }
      }
      return results;
    }
    try {
      const anchor = dayMoment.clone().hour(12).toDate();
      const opts = prepareRRuleOpts(schedule, anchor);
      if (!opts) return results;
      const rr = prebuiltRr || new RRule(opts);
      const dayStart = dayMoment.clone().startOf('day').toDate();
      const dayEnd = dayMoment.clone().endOf('day').toDate();
      if (rr.between(dayStart, dayEnd, true).length > 0) {
        const astroTime = getAstroTimeForDay(schedule, system, dayMoment);
        if (astroTime) {
          const m = moment(astroTime);
          if (m.isBetween(rangeStartM, rangeEndM, 'minute', '[]')) {
            results.push({ date: m.toDate(), time: m.format('HH:mm') });
          }
        }
      }
    } catch (e) {
      // fall through
    }
    return results;
  }

  const baseTime = getBaseTime(schedule, system, dayMoment);
  if (!baseTime) return results;

  const baseM = moment(baseTime);
  const timeOfDay = baseM.format('HH:mm');

  const repeatRule = schedule.r_rule || schedule.rrule;
  if (!repeatRule) {
    // One-time
    const m = baseM.clone();
    if (m.isBetween(moment(rangeStart), moment(rangeEnd), 'minute', '[]')) {
      results.push({ date: m.toDate(), time: timeOfDay });
    }
    return results;
  }

  try {
    let rr = prebuiltRr;
    if (!rr) {
      const opts = prepareRRuleOpts(schedule, baseTime);
      if (!opts) return results;
      rr = new RRule(opts);
    }
    const occs = rr.between(rangeStart, rangeEnd, true);
    for (let i = 0; i < occs.length && i < maxOcc; i++) {
      const m = moment(occs[i]);
      results.push({ date: m.toDate(), time: m.format('HH:mm') });
    }
  } catch (e) {
    const m = baseM.clone();
    if (m.isBetween(moment(rangeStart), moment(rangeEnd), 'minute', '[]')) {
      results.push({ date: m.toDate(), time: timeOfDay });
    }
  }
  return results;
}

// Does this schedule have any occurrence between dayStart and dayEnd (inclusive)?
function hasOccurrencesOnDay(rr, dayMoment) {
  const dayStart = dayMoment.clone().startOf('day').toDate();
  const dayEnd = dayMoment.clone().endOf('day').toDate();
  try {
    return rr.between(dayStart, dayEnd, true).length > 0;
  } catch (e) {
    return false;
  }
}

// Lightweight "does this schedule have any run on this calendar day?" using between().
function scheduleRunsOnDay(schedule, dayMoment, prebuiltRr = null, system = null) {
  if (isAstroSchedule(schedule) && system && hasAstroLocation(system)) {
    const astroTime = getAstroTimeForDay(schedule, system, dayMoment);
    if (!astroTime) return false;

    if (isOneTimeSchedule(schedule)) {
      return performAtMatchesDay(schedule, dayMoment);
    }

    const performAt = schedule.performAt;
    const anchorBase = performAt?.toDate ? performAt.toDate() : (performAt ? new Date(performAt.seconds * 1000) : astroTime);
    if (!dayWithinScheduleBounds(schedule, dayMoment, anchorBase)) return false;

    try {
      const anchor = dayMoment.clone().hour(12).toDate();
      let rr = prebuiltRr;
      if (!rr) {
        const opts = prepareRRuleOpts(schedule, anchor);
        if (!opts) return false;
        rr = new RRule(opts);
      }
      return hasOccurrencesOnDay(rr, dayMoment);
    } catch (e) {
      return performAtMatchesDay(schedule, dayMoment);
    }
  }

  const baseTime = getBaseTime(schedule, system, dayMoment);
  if (!baseTime) return false;

  const repeatRule = schedule.r_rule || schedule.rrule;
  if (!repeatRule) {
    return moment(baseTime).isSame(dayMoment, 'day');
  }

  if (!dayWithinScheduleBounds(schedule, dayMoment, baseTime)) return false;

  if (isHighFrequencyDaily(schedule)) {
    return true;
  }

  try {
    let rr = prebuiltRr;
    if (!rr) {
      const opts = prepareRRuleOpts(schedule, baseTime);
      if (!opts) return false;
      rr = new RRule(opts);
    }
    return hasOccurrencesOnDay(rr, dayMoment);
  } catch (e) {
    return moment(baseTime).isSame(dayMoment, 'day');
  }
}

function getTargetInfo(schedule, scenes) {
  const sceneId = schedule.scene || schedule.cycle;
  if (sceneId) {
    const s = (scenes || []).find((x) => x.uid === sceneId);
    const isCycle = s && getSceneType(s) === 'cycle';
    return {
      name: s ? (isCycle ? `${s.name} (cycle)` : s.name) : 'Scene',
      colors: getScenePreviewColors(s),
    };
  }
  return { name: 'Target', colors: [] };
}

// Time helpers (minutes since midnight of the selected day)
function timeToMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return 0;
  const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10) || 0);
  return h * 60 + m;
}
function minutesToTime(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Sample time for grid (first occurrence on the day via between)
function getSampleTimeOnDay(schedule, dayMoment, prebuiltRr = null, system = null) {
  if (isAstroSchedule(schedule) && system && hasAstroLocation(system)) {
    const astroTime = getAstroTimeForDay(schedule, system, dayMoment);
    if (!astroTime) return '';
    if (!scheduleRunsOnDay(schedule, dayMoment, prebuiltRr, system)) return '';
    return moment(astroTime).format('HH:mm');
  }

  const baseTime = getBaseTime(schedule, system, dayMoment);
  if (!baseTime) return '';

  const repeatRule = schedule.r_rule || schedule.rrule;
  if (!repeatRule) {
    if (moment(baseTime).isSame(dayMoment, 'day')) {
      return moment(baseTime).format('HH:mm');
    }
    return '';
  }

  if (!dayWithinScheduleBounds(schedule, dayMoment, baseTime)) return '';

  if (isHighFrequencyDaily(schedule)) {
    return moment(baseTime).format('HH:mm');
  }

  try {
    const dayStart = dayMoment.clone().startOf('day').toDate();
    const dayEnd = dayMoment.clone().endOf('day').toDate();
    let rr = prebuiltRr;
    if (!rr) {
      const opts = prepareRRuleOpts(schedule, baseTime);
      if (!opts) return '';
      rr = new RRule(opts);
    }
    const occs = rr.between(dayStart, dayEnd, true);
    if (occs.length > 0) {
      return moment(occs[0]).format('HH:mm');
    }
  } catch (e) {}
  return '';
}

// Enrich + collect instances inside a precise small time window on the selected day
function getInstancesForWindow(schedules, dayMoment, winStartMin, winEndMin, scenes, rruleCache = null) {
  const dayBase = dayMoment.clone().startOf('day');
  const winStart = dayBase.clone().add(winStartMin, 'minutes').toDate();
  const winEnd = dayBase.clone().add(winEndMin, 'minutes').toDate();

  const out = [];
  (schedules || []).forEach((sch) => {
    const id = sch.uid || sch;
    const cachedRr = rruleCache ? rruleCache.get(id) : null;
    const occs = getOccurrencesInRange(sch, winStart, winEnd, cachedRr, 40); // guard: only generate enough for display caps in bands
    const target = getTargetInfo(sch, scenes);
    occs.forEach((o) => {
      out.push({
        schedule: sch,
        time: o.time,
        target,
      });
    });
  });
  // Guard: if somehow many across schedules, truncate (UI caps display anyway)
  if (out.length > 1000) {
    out.length = 1000;
  }
  out.sort((a, b) => a.time.localeCompare(b.time));
  return out;
}

const Calendar = ({ schedules = [], scenes = [], currentSystem, onEditSchedule, onSelectedDateChange }) => {
  const [current, setCurrent] = useState(() => moment().startOf('month'));
  const [selectedDate, setSelectedDate] = useState(() => moment());

  // New zoomable day summary state (per approved plan)
  const [periodMinutes, setPeriodMinutes] = useState(60); // 15 | 30 | 60
  const [windowStartMinutes, setWindowStartMinutes] = useState(0); // slot-aligned from 00:00

  // Stable keys (computed only when the array refs change)
  const scheduleKey = useMemo(() => (schedules || []).map((s) => s.uid || '').sort().join(','), [schedules]);
  const scenesKey = useMemo(() => (scenes || []).map((s) => s.uid || '').sort().join(','), [scenes]);
  const systemKey = currentSystem?.uid || '';

  const astroBanner = useMemo(() => {
    if (!currentSystem) return '';
    if (!hasAstroLocation(currentSystem)) {
      return 'Set latitude/longitude in system settings to show sun times.';
    }
    const times = getAstroTimesForDisplay(currentSystem, selectedDate);
    return getPrimaryAstroBanner(times, getSystemTimezone(currentSystem));
  }, [currentSystem, selectedDate, systemKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const astroMarkers = useMemo(() => {
    if (!currentSystem || !hasAstroLocation(currentSystem)) return [];
    const times = getAstroTimesForDisplay(currentSystem, selectedDate);
    if (!times) return [];
    const markers = [];
    if (times.sunrise) markers.push({ label: 'Sunrise', min: timeToMinutes(moment(times.sunrise).format('HH:mm')) });
    if (times.sunset) markers.push({ label: 'Sunset', min: timeToMinutes(moment(times.sunset).format('HH:mm')) });
    return markers;
  }, [currentSystem, selectedDate, systemKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoize the expensive-ish month view range + days array (only when month changes)
  const viewRange = useMemo(() => {
    const startOfView = current.clone().startOf('month').startOf('week');
    const endOfView = current.clone().endOf('month').endOf('week');
    const viewStart = startOfView.clone().startOf('day');
    const viewEnd = endOfView.clone().endOf('day');
    const days = [];
    let d = startOfView.clone();
    while (d.isSameOrBefore(endOfView)) {
      days.push(d.clone());
      d.add(1, 'day');
    }
    return { startOfView, endOfView, viewStart, viewEnd, days };
  }, [current]);

  const { startOfView, endOfView, viewStart, viewEnd, days } = viewRange;

  const selectedKey = selectedDate.format('YYYY-MM-DD');

  // Reset window to default (00:00 + 4 periods) on day or period change.
  // We do it by keying off selectedDate + period in the computations below and explicit setters.

  // Grid data: which distinct schedules run on each visible day. Depends on the viewed month (current).
  const daySchedules = useMemo(() => {
    if (!schedules || schedules.length === 0) return {};
    const dScheds = {};
    // reuse the already-memoized days list (avoids duplicate 42-day moment construction)
    const visibleDays = days;

    // Prebuild RRule only for non-dense (see below). For high-freq daily we avoid rrule entirely in grid.
    const rruleCache = new Map();

    // Static guard: detect dense high-freq daily schedules *without* executing rrule or generating occurrences.
    // Inspects the rule options. For these (e.g. every 5 min daily), we treat as running on (nearly) all days
    // in the view without any per-day probes or sampling. This is the key guard for perf with minutely rules.
    const denseIds = new Set();
    (schedules || []).forEach((sch) => {
      const id = sch.uid || sch;
      if (isHighFrequencyDaily(sch)) {
        denseIds.add(id);
      } else {
        // only build cache for ones we will probe
        const baseTime = getBaseTime(sch, currentSystem, null);
        if (baseTime && (sch.r_rule || sch.rrule)) {
          try {
            const opts = prepareRRuleOpts(sch, baseTime);
            if (opts) {
              rruleCache.set(id, new RRule(opts));
            }
          } catch (e) {}
        }
      }
    });

    (schedules || []).forEach((sch) => {
      const id = sch.uid || sch;
      const cachedRr = rruleCache.get(id);
      const isDense = denseIds.has(id);
      visibleDays.forEach((dayM) => {
        let runs = false;
        if (isDense) {
          runs = dayWithinScheduleBounds(sch, dayM, getBaseTime(sch, currentSystem, dayM));
        } else {
          runs = scheduleRunsOnDay(sch, dayM, cachedRr, currentSystem);
        }
        if (runs) {
          const key = dayM.format('YYYY-MM-DD');
          if (!dScheds[key]) dScheds[key] = [];
          if (!dScheds[key].some((e) => e.schedule?.uid === sch.uid || e.schedule === sch)) {
            const tgt = getTargetInfo(sch, scenes);
            const sampleTime = getSampleTimeOnDay(sch, dayM, cachedRr, currentSystem);
            dScheds[key].push({ schedule: sch, target: tgt, sampleTime });
          }
        }
      });
    });
    return dScheds;
  }, [scheduleKey, scenesKey, current, systemKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Windowed day summary instances + label. Cheap because the time window is small (4 periods).
  const { windowInstances, currentWindowLabel, winStartMin, periodMin } = useMemo(() => {
    if (!schedules || schedules.length === 0) {
      return { windowInstances: [], currentWindowLabel: '00:00 – 04:00 (4 × 60m)', winStartMin: 0, periodMin: 60 };
    }
    const pMin = periodMinutes;
    const wStart = windowStartMinutes;
    const sDay = selectedDate.clone();
    const wEnd = Math.min(24 * 60, wStart + 4 * pMin);
    const day0 = sDay.clone().startOf('day');
    const winStartD = day0.clone().add(wStart, 'minutes').toDate();
    const winEndD = day0.clone().add(wEnd, 'minutes').toDate();

    // Identify dense high-freq (no rrule needed for them)
    const denseIds = new Set();
    (schedules || []).forEach((sch) => {
      if (isHighFrequencyDaily(sch)) denseIds.add(sch.uid || sch);
    });

    const winInsts = [];
    (schedules || []).forEach((sch) => {
      const id = sch.uid || sch;
      if (denseIds.has(id)) {
        const baseTime = getBaseTime(sch, currentSystem, sDay);
        if (!baseTime) return;
        if (!dayWithinScheduleBounds(sch, sDay, baseTime)) return;
        const raw = (sch.r_rule || sch.rrule || {}).state || (sch.r_rule || sch.rrule) || {};
        let intervalMin = Number(raw.interval) || 5;
        const f = Number(raw.freq);
        if (f === 4 /* hourly */) intervalMin *= 60;
        const baseM = moment(baseTime);
        const baseMin = baseM.hours() * 60 + baseM.minutes();
        // smallest curMin >= wStart such that curMin ≡ baseMin (mod intervalMin)
        let diff = (wStart - baseMin) % intervalMin;
        if (diff < 0) diff += intervalMin;
        const add = (intervalMin - diff) % intervalMin;
        let curMin = wStart + add;
        const maxT = wEnd;
        let cnt = 0;
        while (curMin < maxT && cnt < 100) {
          const t = day0.clone().add(curMin, 'minutes');
          winInsts.push({
            schedule: sch,
            time: t.format('HH:mm'),
            target: getTargetInfo(sch, scenes)
          });
          curMin += intervalMin;
          cnt++;
        }
        return;
      }
      // normal (non-dense) path - build RRule only for these
      const baseTime = getBaseTime(sch, currentSystem, sDay);
      let cachedRr = null;
      if (baseTime && (sch.r_rule || sch.rrule) && !isAstroSchedule(sch)) {
        try {
          const opts = prepareRRuleOpts(sch, baseTime);
          if (opts) cachedRr = new RRule(opts);
        } catch (e) {}
      } else if (isAstroSchedule(sch) && (sch.r_rule || sch.rrule)) {
        try {
          const anchor = sDay.clone().hour(12).toDate();
          const opts = prepareRRuleOpts(sch, anchor);
          if (opts) cachedRr = new RRule(opts);
        } catch (e) {}
      }
      const occs = getOccurrencesInRange(sch, winStartD, winEndD, cachedRr, 40, currentSystem);
      const tgt = getTargetInfo(sch, scenes);
      occs.forEach((o) => {
        winInsts.push({ schedule: sch, time: o.time, target: tgt });
      });
    });

    // Ensure instances are ordered by run time (across all schedules)
    winInsts.sort((a, b) => a.time.localeCompare(b.time));

    const startLabel = minutesToTime(wStart);
    const endLabel = minutesToTime(wEnd);
    const label = `${startLabel} – ${endLabel} (4 × ${pMin}m)`;

    return {
      windowInstances: winInsts,
      currentWindowLabel: label,
      winStartMin: wStart,
      periodMin: pMin,
    };
  }, [scheduleKey, scenesKey, selectedDate, periodMinutes, windowStartMinutes, systemKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const goPrev = () => setCurrent(current.clone().subtract(1, 'month'));
  const goNext = () => setCurrent(current.clone().add(1, 'month'));
  const goToday = () => setCurrent(moment().startOf('month'));

  const todayKey = moment().format('YYYY-MM-DD');

  // Day summary control handlers (period + pageable 4-period windows)
  const handleSetPeriod = (mins) => {
    setPeriodMinutes(mins);
    setWindowStartMinutes(0);
  };
  const pageWindow = (direction /* +1 or -1 */) => {
    const step = 4 * periodMinutes;
    let next = windowStartMinutes + direction * step;
    if (next >= 24 * 60) next = 0;
    if (next < 0) next = Math.max(0, 24 * 60 - 4 * periodMinutes);
    setWindowStartMinutes(next);
  };
  const resetToMidnight = () => setWindowStartMinutes(0);

  // When selecting a day from grid, default the window back to start of day
  const selectThisDay = (dayM) => {
    setSelectedDate(dayM.clone());
    setWindowStartMinutes(0);
    if (onSelectedDateChange) {
      onSelectedDateChange(dayM.toDate());
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5">
          <button
            onClick={goPrev}
            className="px-2 py-1 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700"
          >
            ←
          </button>
          <button
            onClick={goToday}
            className="px-2 py-0.5 text-xs border rounded hover:bg-gray-100 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700"
          >
            Today
          </button>
          <button
            onClick={goNext}
            className="px-2 py-1 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700"
          >
            →
          </button>
        </div>
        <div className="font-semibold text-sm">{current.format('MMMM YYYY')}</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400">
          {schedules.length} schedule{schedules.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:items-start lg:min-h-[480px]">
        {/* Left: month grid — shows only distinct schedules per day (no instances) */}
        <div className="flex-1 min-w-0 w-full">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 text-center text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dw) => (
              <div key={dw}>{dw}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden text-xs">
            {days.map((day) => {
              const key = day.format('YYYY-MM-DD');
              const isCurrentMonth = day.month() === current.month();
              const isToday = key === todayKey;
              const isSelected = key === selectedKey;
              const schedsForDay = daySchedules[key] || [];

              return (
                <div
                  key={key}
                  onClick={() => selectThisDay(day)}
                  className={`min-h-[64px] sm:min-h-[82px] bg-white dark:bg-gray-900 p-0.5 sm:p-1 cursor-pointer ${!isCurrentMonth ? 'opacity-40' : ''} ${isSelected ? 'ring-2 ring-[#880e4f] bg-[#880e4f]/5 dark:bg-[#880e4f]/10' : ''} ${isToday ? 'ring-1 ring-[#880e4f] ring-inset' : ''}`}
                >
                  <div className={`font-medium mb-0.5 text-[11px] ${isToday ? 'text-[#880e4f]' : 'text-gray-700 dark:text-gray-300'}`}>
                    {day.date()}
                  </div>

                  {schedsForDay.length > 0 && (
                    <div className="space-y-0.5">
                      {schedsForDay.slice(0, 3).map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded px-1 py-[1px] overflow-hidden cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                          title={`${item.schedule?.name || ''} — ${item.target.name}${item.sampleTime ? ' @ ' + item.sampleTime : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            selectThisDay(day);
                            if (onEditSchedule && item.schedule) onEditSchedule(item.schedule);
                          }}
                        >
                          {item.target.colors && item.target.colors.length > 0 && (
                            <SVGColorBlock size={12} data={item.target.colors} />
                          )}
                          <span className="text-[9px] leading-tight truncate text-gray-700 dark:text-gray-300">
                            {item.schedule?.name || item.target.name}
                          </span>
                        </div>
                      ))}
                      {schedsForDay.length > 3 && (
                        <div className="text-[9px] text-gray-500 dark:text-gray-400 pl-1">
                          +{schedsForDay.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: zoomable day summary — exactly 4 pageable periods, only instances in current window */}
        <div className="w-full lg:w-72 lg:flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 pt-4 lg:pt-0 lg:pl-4 flex flex-col">
          <div className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300 flex-shrink-0">
            {selectedDate.format('dddd, MMMM D')}
          </div>

          {astroBanner && (
            <div className="text-[10px] text-gray-600 dark:text-gray-400 mb-2 flex-shrink-0 leading-relaxed">
              {astroBanner}
            </div>
          )}

          {/* Controls: granularity + paging (no full-day scroll) */}
          <div className="flex flex-wrap items-center gap-1 mb-2 flex-shrink-0">
            <div className="text-[10px] mr-1 text-gray-500">Period:</div>
            {[15, 30, 60].map((m) => (
              <button
                key={m}
                onClick={() => handleSetPeriod(m)}
                className={`text-[10px] px-1.5 py-0.5 rounded border ${periodMinutes === m ? 'bg-[#880e4f] text-white border-[#880e4f]' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                {m}m
              </button>
            ))}
            <div className="flex-1" />
            <button onClick={() => pageWindow(-1)} className="text-[10px] px-1.5 py-0.5 border rounded hover:bg-gray-100 dark:hover:bg-gray-800">← 4</button>
            <button onClick={resetToMidnight} className="text-[10px] px-1 py-0.5 border rounded hover:bg-gray-100 dark:hover:bg-gray-800" title="Reset to 00:00">0:00</button>
            <button onClick={() => pageWindow(1)} className="text-[10px] px-1.5 py-0.5 border rounded hover:bg-gray-100 dark:hover:bg-gray-800">4 →</button>
          </div>

          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 tabular-nums flex-shrink-0">
            {currentWindowLabel}
          </div>

          {/* 4 fixed period bands with horizontal time lines - scrollable when many instances */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900 max-h-[600px]">
            {Array.from({ length: 4 }).map((_, i) => {
              const slotStart = winStartMin + i * periodMin;
              const slotEnd = slotStart + periodMin;
              const label = minutesToTime(slotStart);
              const endLabel = minutesToTime(slotEnd);

              // Guarded collection: only keep up to 8 for this slot + count more. Avoids large temp arrays for dense windows.
              const visible = [];
              let more = 0;
              for (const ev of (windowInstances || [])) {
                const tmin = timeToMinutes(ev.time);
                if (tmin >= slotStart && tmin < slotEnd) {
                  if (visible.length < 8) {
                    visible.push(ev);
                  } else {
                    more++;
                  }
                }
              }

              return (
                <div key={i} className="flex items-start min-h-[66px] text-xs relative">
                  <div className="w-16 flex-shrink-0 px-1.5 py-1 text-[10px] font-mono border-r border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
                    {label}<br /><span className="opacity-60">–{endLabel}</span>
                  </div>
                  <div className="flex-1 p-1 flex flex-wrap gap-1 content-start overflow-hidden relative">
                    {astroMarkers.filter((m) => m.min >= slotStart && m.min < slotEnd).map((m) => (
                      <div
                        key={m.label}
                        className="absolute left-0 right-0 border-t border-amber-400/50 pointer-events-none"
                        style={{ top: `${Math.max(4, ((m.min - slotStart) / periodMin) * 100)}%` }}
                        title={m.label}
                      />
                    ))}
                    {visible.length === 0 && (
                      <span className="text-gray-400 italic px-1 self-center">—</span>
                    )}
                    {visible.map((ev, j) => (
                      <div
                        key={j}
                        onClick={() => onEditSchedule && onEditSchedule(ev.schedule)}
                        className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded px-1 py-0.5 cursor-pointer"
                        title={`${ev.schedule?.name} — ${ev.target.name}`}
                      >
                        {ev.target.colors && ev.target.colors.length > 0 && (
                          <SVGColorBlock size={12} data={ev.target.colors} />
                        )}
                        <span className="tabular-nums text-[10px]">{ev.time}</span>
                        <span className="truncate max-w-[96px]">{ev.schedule?.name || ev.target.name}</span>
                      </div>
                    ))}
                    {more > 0 && (
                      <span className="text-gray-500 dark:text-gray-400 self-center px-1 text-[10px]">+{more} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {windowInstances.length === 0 && (
            <div className="text-[10px] mt-1 text-gray-500 dark:text-gray-400 italic">No instances in this 4-period window.</div>
          )}
        </div>
      </div>

      <div className="mt-2 text-[10px] text-gray-500 dark:text-gray-400">
        Calendar shows schedules (not every run). Day view shows only the current 4 periods — use period size and ←4 / 4→ to page the day.
      </div>
    </div>
  );
};

export default Calendar;